<template>
    <div id="netgsm-cdr">
        <div class="page-header">
            <div class="left">
                <h3>Arama Kayıtları (CDR)</h3>
                <p>NetGSM arama detay kayıtlarını görüntüleyin</p>
            </div>
            <div class="right">
                <a-button @click="refreshData" :loading="loading" :icon="'reload'"> Yenile </a-button>
                <a-button type="primary" @click="exportCDR" :loading="loading" :icon="'download'">
                    Dışa Aktar
                </a-button>
            </div>
        </div>

        <!-- Advanced Filters -->
        <div class="toolbar">
            <div class="toolbar-filter">
                <a-input-search
                    style="width: 300px"
                    v-model="searchText"
                    placeholder="CDR ID, Numara veya Unique ID ile ara..."
                    class="search-input"
                    :loading="loading"
                    @search="onSearch"
                />
                <!-- Date Range Filter (Always visible and required) -->
                <div class="date-filter-inline">
                    <span class="date-label">Tarih (Max 1 gün):</span>
                    <a-date-picker
                        v-model="selectedDate"
                        format="DD.MM.YYYY"
                        style="width: 200px"
                        placeholder="Tarih seçin"
                        @change="onDateChange"
                    />
                    <span class="date-required">*</span>
                    <small class="date-help">NetGSM API'si maksimum 1 günlük aralığı destekler</small>
                </div>
            </div>
            <div class="toolbar-actions"></div>
        </div>

        <!-- Statistics -->
        <div class="statistics">
            <div class="stats-grid">
                <div class="stat-card total-calls">
                    <div class="stat-icon">
                        <i class="material-icons">call</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">{{ statistics.totalCalls }}</div>
                        <div class="stat-label">Toplam Arama</div>
                    </div>
                </div>

                <div class="stat-card answered-calls">
                    <div class="stat-icon">
                        <i class="material-icons">call_received</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">{{ statistics.answeredCalls }}</div>
                        <div class="stat-label">Cevaplanmış</div>
                        <div class="stat-percentage">
                            {{
                                statistics.totalCalls > 0
                                    ? Math.round((statistics.answeredCalls / statistics.totalCalls) * 100)
                                    : 0
                            }}%
                        </div>
                    </div>
                </div>

                <div class="stat-card total-duration">
                    <div class="stat-icon">
                        <i class="material-icons">schedule</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">{{ statistics.totalDuration }}</div>
                        <div class="stat-label">Toplam Süre</div>
                        <div class="stat-unit">dakika</div>
                    </div>
                </div>

                <div class="stat-card average-duration">
                    <div class="stat-icon">
                        <i class="material-icons">timer</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">{{ statistics.averageDuration }}</div>
                        <div class="stat-label">Ortalama Süre</div>
                        <div class="stat-unit">dakika</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- CDR Table -->
        <div class="content">
            <SupsisTable
                :columns="getCols()"
                :rows="cdrRecords"
                :loading="loading"
                top-pagination
                bottom-pagination
                :pagination-cursor="pagination"
                :options="{
                    fixedHeader: true,
                    borderX: true,
                    borderY: true,
                }"
                @cursor-changed="onPaginationChange"
                :pagination-options="[10, 20, 30, 40, 50, 100, 200, 300, 400, 500, 1000]"
            />
        </div>

        <!-- Call Detail Modal -->
        <a-modal v-model="showDetailModal" title="Arama Detayları" :footer="null" width="700px">
            <div v-if="selectedCall">
                <a-descriptions :column="2" bordered size="small">
                    <a-descriptions-item label="CDR ID">
                        {{ selectedCall.cdrid }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Tarih">
                        {{ $moment(selectedCall.calldate).format("DD.MM.YYYY HH:mm:ss") }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Arayan (CLI)">
                        {{ selectedCall.cli }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Aranan (CLD)">
                        {{ selectedCall.cld }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Yön">
                        <a-tag :color="getDirectionColor(selectedCall.direction)">
                            {{ getDirectionText(selectedCall.direction) }}
                        </a-tag>
                    </a-descriptions-item>
                    <a-descriptions-item label="Durum">
                        <a-tag :color="getStatusColor(selectedCall.status)">
                            {{ selectedCall.status }}
                        </a-tag>
                    </a-descriptions-item>
                    <a-descriptions-item label="Toplam Süre">
                        {{ formatDuration(selectedCall.duration) }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Konuşma Süresi">
                        {{ formatDuration(selectedCall.billsec) }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Unique ID" span="2">
                        {{ selectedCall.uniqueid }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Kayıt Dosyası" span="2" v-if="selectedCall.recordurl">
                        <a :href="selectedCall.recordurl" target="_blank" class="recording-link">
                            <i class="material-icons">play_circle</i>
                            Kayıt İndir
                        </a>
                    </a-descriptions-item>
                </a-descriptions>
            </div>
        </a-modal>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import { WEBPHONE_NETGSM_API } from "../../../../../api/webphone.netgsm";
import log2 from "../../../../../utils/log2";

const log = log2("NetGsmCDR");

export default Vue.extend({
    name: "NetGsmCDR",
    components: {
        SupsisTable,
        SupsisButton,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            showDetailModal: false,
            selectedCall: null,
            cdrRecords: [],
            searchText: "",
            selectedDate: moment(), // Default to today
            pagination: {
                page: 1,
                perPage: 1000,
                total: 0,
            },
            statistics: {
                totalCalls: 0,
                answeredCalls: 0,
                totalDuration: 0,
                averageDuration: 0,
            },
        };
    },
    methods: {
        getCols() {
            return [
                {
                    key: "calldate",
                    field: "calldate",
                    title: "Tarih/Saat",
                    width: 140,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <span>{this.$moment(row.calldate).format("DD.MM.YYYY HH:mm")}</span>;
                    },
                },
                {
                    key: "direction",
                    field: "direction",
                    title: "Yön",
                    width: 80,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return (
                            <a-tag color={this.getDirectionColor(row.direction)}>
                                {this.getDirectionText(row.direction)}
                            </a-tag>
                        );
                    },
                },
                {
                    key: "cli",
                    field: "cli",
                    title: "Arayan",
                    width: 120,
                    align: "left",
                },
                {
                    key: "cld",
                    field: "cld",
                    title: "Aranan",
                    width: 120,
                    align: "left",
                },
                {
                    key: "status",
                    field: "status",
                    title: "Durum",
                    width: 100,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return <a-tag color={this.getStatusColor(row.status)}>{row.status}</a-tag>;
                    },
                },
                {
                    key: "duration",
                    field: "duration",
                    title: "Toplam Süre",
                    width: 100,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return <span>{this.formatDuration(row.duration)}</span>;
                    },
                },
                {
                    key: "billsec",
                    field: "billsec",
                    title: "Konuşma",
                    width: 90,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return <span>{this.formatDuration(row.billsec)}</span>;
                    },
                },
                {
                    title: "İşlemler",
                    key: "actions",
                    width: 80,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return (
                            <SupsisButton
                                onClick={() => this.viewDetails(row)}
                                small
                                content="Detay Görüntüle"
                                v-tippy={{ followCursor: true, placement: "bottom-end" }}
                            >
                                <i class="material-icons text-primary">visibility</i>
                            </SupsisButton>
                        );
                    },
                },
            ];
        },
        async loadCDRData() {
            if (!this.selectedDate) {
                this.$message.error("Lütfen tarih seçin");
                return;
            }

            try {
                this.loading = true;

                const selectedDateStr = this.selectedDate.format("YYYY-MM-DD");
                const filters = {
                    startdate: selectedDateStr,
                    enddate: selectedDateStr, // Aynı gün (1 günlük aralık)
                    limit: this.pagination.perPage.toString(),
                    offset: ((this.pagination.page - 1) * this.pagination.perPage).toString(),
                    querytype: "0", // 0: arayan no, 1: aranan no
                    no: [], // Boş array = tüm numaralar
                };

                // Arama terimi varsa no array'ine ekle
                if (this.searchText && this.searchText.trim()) {
                    // Sadece numara ise no array'ine ekle
                    const searchTerm = this.searchText.trim();
                    if (/^\d+$/.test(searchTerm)) {
                        filters.no = [searchTerm];
                    } else {
                        // Numara değilse client-side filtreleme yapacağız
                        filters.searchText = searchTerm;
                    }
                }

                log("Loading NetGSM CDR with filters:", filters);

                const response = await WEBPHONE_NETGSM_API.reports.getCDRList(filters);

                if (response && response.cdrs) {
                    let cdrRecords = response.cdrs.map((cdr, index) => ({
                        ...cdr,
                        _id: cdr.uniqueid || index.toString(),
                        // Convert duration strings to numbers
                        duration: parseInt(cdr.duration) || 0,
                        billsec: parseInt(cdr.billsec) || 0,
                    }));

                    // Client-side filtreleme (numara olmayan arama terimleri için)
                    if (this.searchText && this.searchText.trim() && !/^\d+$/.test(this.searchText.trim())) {
                        const searchTerm = this.searchText.trim().toLowerCase();
                        cdrRecords = cdrRecords.filter(cdr => 
                            (cdr.uniqueid && cdr.uniqueid.toLowerCase().includes(searchTerm)) ||
                            (cdr.cdrid && cdr.cdrid.toLowerCase().includes(searchTerm)) ||
                            (cdr.cli && cdr.cli.toLowerCase().includes(searchTerm)) ||
                            (cdr.cld && cdr.cld.toLowerCase().includes(searchTerm))
                        );
                    }

                    this.cdrRecords = cdrRecords;

                    // Update pagination with new total (reactive update)
                    this.pagination = {
                        ...this.pagination,
                        total: response.total || this.cdrRecords.length,
                    };
                } else {
                    this.cdrRecords = [];
                    this.pagination = {
                        ...this.pagination,
                        total: 0,
                    };
                }

                this.calculateStatistics(this.cdrRecords);
            } catch (error) {
                console.error("CDR verileri yüklenirken hata:", error);
                this.$message.error("CDR verileri yüklenirken hata oluştu: " + (error.response?.data?.error || error.message));
                this.cdrRecords = [];
                this.pagination.total = 0;
            } finally {
                this.loading = false;
            }
        },
        calculateStatistics(records) {
            this.statistics.totalCalls = records.length;
            this.statistics.answeredCalls = records.filter((r) => r.status === "ANSWERED").length;

            const totalDurationSeconds = records.reduce((sum, call) => sum + (call.billsec || 0), 0);
            this.statistics.totalDuration = Math.round((totalDurationSeconds / 60) * 10) / 10;

            const answeredCalls = records.filter((r) => r.status === "ANSWERED");
            if (answeredCalls.length > 0) {
                const avgDurationSeconds =
                    answeredCalls.reduce((sum, call) => sum + (call.billsec || 0), 0) / answeredCalls.length;
                this.statistics.averageDuration = Math.round((avgDurationSeconds / 60) * 10) / 10;
            } else {
                this.statistics.averageDuration = 0;
            }
        },
        onDateChange() {
            this.pagination.page = 1;
            this.loadCDRData();
        },
        onSearch() {
            this.pagination.page = 1;
            this.loadCDRData();
        },
        onPaginationChange(newPagination) {
            this.pagination = {
                ...newPagination,
                total: this.pagination.total,
            };
            this.loadCDRData();
        },
        refreshData() {
            this.loadCDRData();
        },
        exportCDR() {
            if (this.cdrRecords.length === 0) {
                this.$message.warning("Dışa aktarılacak veri bulunamadı");
                return;
            }

            const csvContent = this.generateCSV(this.cdrRecords);
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `netgsm_cdr_export_${moment().format("YYYY-MM-DD")}.csv`);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            this.$message.success(`${this.cdrRecords.length} kayıt dışa aktarıldı`);
        },
        generateCSV(data) {
            const headers = [
                "CDR ID",
                "Tarih",
                "Yön",
                "Arayan",
                "Aranan",
                "Durum",
                "Toplam Süre",
                "Konuşma Süresi",
            ];
            const rows = data.map((record) => [
                record.cdrid,
                moment(record.calldate).format("DD.MM.YYYY HH:mm:ss"),
                this.getDirectionText(record.direction),
                record.cli,
                record.cld,
                record.status,
                this.formatDuration(record.duration),
                this.formatDuration(record.billsec),
            ]);

            return [headers, ...rows].map((row) => row.join(",")).join("\n");
        },
        viewDetails(call) {
            this.selectedCall = call;
            this.showDetailModal = true;
        },
        getDirectionText(direction) {
            const directions = {
                INBOUND: "Gelen",
                OUTBOUND: "Giden",
            };
            return directions[direction] || direction;
        },
        getDirectionColor(direction) {
            const colors = {
                INBOUND: "green",
                OUTBOUND: "blue",
            };
            return colors[direction] || "default";
        },
        getStatusColor(status) {
            const colors = {
                ANSWERED: "green",
                "NO ANSWER": "orange",
                BUSY: "red",
                FAILED: "red",
            };
            return colors[status] || "default";
        },
        formatDuration(seconds) {
            const duration = parseInt(seconds) || 0;
            if (duration === 0) return "0s";
            const minutes = Math.floor(duration / 60);
            const secs = duration % 60;
            return minutes > 0 ? `${minutes}dk ${secs}s` : `${secs}s`;
        },
    },
    mounted() {
        this.loadCDRData();
    },
});
</script>

<style scoped lang="scss">
#netgsm-cdr {
    padding: 20px;
    background: var(--color-bg-default);
    height: 100%;

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .left {
            h3 {
                margin: 0 0 5px 0;
                color: var(--color-text-primary);
                font-size: 20px;
                font-weight: 600;
            }

            p {
                margin: 0;
                color: var(--color-text-secondary);
                font-size: 14px;
            }
        }

        .right {
            display: flex;
            gap: 10px;
        }
    }

    .toolbar {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 0;
        gap: 5px;
        margin-bottom: 20px;

        .toolbar-filter {
            display: flex;
            align-items: center;
            gap: 15px;
            flex: 1;
            flex-wrap: wrap;

            .search-input {
                margin: 0 !important;
            }

            .date-filter-inline {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 12px;
                background: #f5f5f5;
                border-radius: 6px;
                border: 1px solid #d9d9d9;

                .date-label {
                    font-weight: 500;
                    color: #333;
                    white-space: nowrap;
                }

                .date-required {
                    color: #ff4d4f;
                    font-weight: bold;
                }
            }
        }

        .toolbar-actions {
            display: flex;
            gap: 5px;
        }
    }

    .statistics {
        margin-bottom: 20px;

        .stats-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;

            .stat-card {
                background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
                border-radius: 16px;
                padding: 24px;
                display: flex;
                align-items: center;
                gap: 20px;
                box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
                border: 1px solid #e2e8f0;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                position: relative;
                overflow: hidden;

                &::before {
                    content: "";
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    transition: height 0.3s ease;
                }

                &:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);

                    &::before {
                        height: 6px;
                    }

                    .stat-icon {
                        transform: scale(1.1);
                    }
                }

                &.total-calls {
                    &::before {
                        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                        color: white;
                    }

                    .stat-value {
                        color: #1d4ed8;
                    }
                }

                &.answered-calls {
                    &::before {
                        background: linear-gradient(135deg, #10b981, #059669);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #10b981, #059669);
                        color: white;
                    }

                    .stat-value {
                        color: #059669;
                    }

                    .stat-percentage {
                        color: #10b981;
                        font-size: 12px;
                        font-weight: 600;
                        margin-top: 4px;
                    }
                }

                &.total-duration {
                    &::before {
                        background: linear-gradient(135deg, #8b5cf6, #7c3aed);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #8b5cf6, #7c3aed);
                        color: white;
                    }

                    .stat-value {
                        color: #7c3aed;
                    }
                }

                &.average-duration {
                    &::before {
                        background: linear-gradient(135deg, #f59e0b, #d97706);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #f59e0b, #d97706);
                        color: white;
                    }

                    .stat-value {
                        color: #d97706;
                    }
                }

                .stat-icon {
                    width: 56px;
                    height: 56px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    transition: all 0.3s ease;

                    .material-icons {
                        font-size: 28px;
                    }
                }

                .stat-content {
                    flex: 1;
                    min-width: 0;

                    .stat-value {
                        font-size: 32px;
                        font-weight: 700;
                        line-height: 1.2;
                        margin-bottom: 4px;
                        font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif;
                    }

                    .stat-label {
                        font-size: 14px;
                        font-weight: 500;
                        color: #64748b;
                        margin-bottom: 2px;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }

                    .stat-unit {
                        font-size: 12px;
                        color: #94a3b8;
                        font-weight: 500;
                    }
                }
            }
        }
    }

    .content {
        background: var(--color-bg-container);
        border-radius: 6px;
        border: 1px solid var(--color-border-default);
        overflow: hidden;
    }

    .recording-link {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        color: #007c89;
        text-decoration: none;
        font-weight: 500;
        transition: all 0.2s ease;

        &:hover {
            color: #005a66;
            text-decoration: underline;
        }

        .material-icons {
            font-size: 18px;
        }
    }

    .date-help {
        color: #666;
        font-size: 11px;
        margin-left: 8px;
        font-style: italic;
    }
}
</style>
